import {
  PaymentAshIcon,
  TransactionAddAshIcon,
  GlobeAshIcon,
  TransactionUpdateAshIcon,
  UseFundsAshIcon
} from '@glu/icons-react';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import template from 'lodash/template';
import sortBy from 'lodash/sortBy';
import React from 'react';

export const ROOT_VALUE = 'name';
export const NESTED_POSITIVE_NEGATIVE_VALUE = 'passed';
export const ACTUAL_STEP = 'currentStep';

export const MAPS = {
  WIRE: 'WIRE',
  ACH: 'ACH'
};

const STEP = {
  PAYMENT_TYPE: 'Payment Type',
  MAP_DEFINITION: 'Map Definition',
  MAPPING_DETAILS: 'Mapping Details'
};

export const steps = (step, mapType, permissions) => {
  const mapDefinitionStep = {
    id: 2,
    name: STEP.MAP_DEFINITION,
    passed: step > 2,
    currentStep: step === 2
  };

  return [
    {
      id: 1,
      name: STEP.PAYMENT_TYPE,
      passed: step > 1,
      currentStep: step === 1
    },
    ...(mapType !== MAPS.WIRE && permissions.includes('ACH') ? [mapDefinitionStep] : []),
    {
      id: 3,
      name: STEP.MAPPING_DETAILS,
      passed: false,
      currentStep: step === 3
    }
  ];
};

export const PAYMENT_TYPE_NAMES = {
  DOMESTIC_WIRE: 'Domestic Wire',
  INTERNATIONAL_WIRE: 'International Wire',
  ACH: 'ACH'
};

export const MAP_TYPE_NAMES = (locale) => ({
  ADD_TRANSACTIONS: locale['wireImports.addTransactions'],
  UPDATE_TRANSACTIONS: locale['wireImports.updateTransactions'],
  DELETE_UNMATCHED_TRANSACTIONS: locale['wireImports.deleteUnmatchedTransactions']
});

const TRANSFORM_IMPORT_PERMISSIONS_TO_PAYMENT_TYPES = {
  MTMAP: [PAYMENT_TYPE_NAMES.DOMESTIC_WIRE, PAYMENT_TYPE_NAMES.INTERNATIONAL_WIRE],
  ACHMAPDEF: [PAYMENT_TYPE_NAMES.ACH]
};

export const MAP_TYPES = (locale) => {
  const { ADD_TRANSACTIONS, UPDATE_TRANSACTIONS } = MAP_TYPE_NAMES(locale);
  return [
    {
      label: ADD_TRANSACTIONS,
      icon: <TransactionAddAshIcon />
    },
    {
      label: UPDATE_TRANSACTIONS,
      icon: <TransactionUpdateAshIcon />
    }
  ];
};

export const TRANSCRIPTS_WIRE_TYPES = {
  'Domestic Wire': 'D',
  'International Wire': 'I',
  ACH: 'A'
};

export const PROCESS_MODES_NAMES = {
  ADD_ONLY: 'ADD_ONLY',
  ADD_AND_UPDATE: 'ADD_AND_UPDATE',
  ADD_UPDATE_DELETE: 'ADD_UPDATE_DELETE',
  UPDATE_ONLY: 'UPDATE_ONLY'
};

const PROCESS_MODES = {
  [PROCESS_MODES_NAMES.ADD_ONLY]: ['Add Transactions'],
  [PROCESS_MODES_NAMES.ADD_AND_UPDATE]: ['Add Transactions', 'Update Transactions'],
  [PROCESS_MODES_NAMES.ADD_UPDATE_DELETE]: [
    'Add Transactions',
    'Update Transactions',
    'Delete Unmatched Transactions'
  ],
  [PROCESS_MODES_NAMES.UPDATE_ONLY]: ['Update Transactions']
};

export const getProcessMode = (mapTypes) =>
  Object.keys(PROCESS_MODES).find((mode) =>
    isEqual(sortBy(PROCESS_MODES[mode]), sortBy(mapTypes))
  );

export const transformPaymentTypeToWire = (paymentType) =>
  paymentType?.length ? TRANSCRIPTS_WIRE_TYPES[paymentType[0]] : '';

export const transformPermissionsToPaymentTypes = (permissions) =>
  flatten(
    permissions.map((permission) => TRANSFORM_IMPORT_PERMISSIONS_TO_PAYMENT_TYPES[permission])
  );

export const PAYMENT_TYPES = ({ locale, setFormStepComponent, setStep }) => [
  {
    label: locale['wireImports.domesticWire'],
    icon: <PaymentAshIcon />,
    onClick: () => {
      setStep(3);
      setFormStepComponent(MAPS.WIRE);
    }
  },
  {
    label: locale['wireImports.internationalWire'],
    icon: <GlobeAshIcon />,
    onClick: () => {
      setStep(3);
      setFormStepComponent(MAPS.WIRE);
    }
  },
  {
    label: locale['wireImports.ach'],
    icon: <UseFundsAshIcon />,
    onClick: ({ selectedData: paymentType }) => {
      if (!paymentType.length) {
        return setStep(1);
      }

      return setStep(2);
    }
  }
];

export const onModifyMode = (mapCode, setStep, setFormStepComponent) => {
  setStep(3);
  return setFormStepComponent(mapCode ? MAPS.ACH : MAPS.WIRE);
};

export const headerDeterminedFor = {
  'International Wire': 'forInternationalWire',
  'Domestic Wire': 'forDomesticWire',
  ACH: 'forACH'
};

export const getHeaderLabel = (achImportMapData, paymentType, modify, activeStep, locale) => {
  if (activeStep !== 3) {
    return template(locale['wireImports.createImportMap'])({
      determinedFor: ''
    });
  }

  if (modify) {
    const headerData = paymentType?.[0] || achImportMapData?.achImportMapHeader?.paymentType;

    return template(locale['wireImports.modifyImportMap'])({
      determinedFor: locale[`wireImports.${headerDeterminedFor[headerData]}`]
    });
  }

  return template(locale['wireImports.createImportMap'])({
    determinedFor: locale[`wireImports.${headerDeterminedFor[paymentType?.[0]]}`]
  });
};

// STEPPER
export const MOVE_ACTIONS = {
  ONE_TWO: 'ONE_TWO',
  ONE_THREE: 'ONE_THREE',
  TWO_ONE: 'TWO_ONE',
  TWO_THREE: 'TWO_THEE',
  THREE_TWO: 'THREE_TWO',
  THREE_ONE: 'THREE_ONE'
};

const STEPPER_MOVE_OPTIONS = [
  {
    action: MOVE_ACTIONS.ONE_TWO,
    currentStep: 1,
    nextStep: 2
  },
  {
    action: MOVE_ACTIONS.ONE_THREE,
    currentStep: 1,
    nextStep: 3
  },
  {
    action: MOVE_ACTIONS.TWO_ONE,
    currentStep: 2,
    nextStep: 1
  },
  {
    action: MOVE_ACTIONS.TWO_THREE,
    currentStep: 2,
    nextStep: 3
  },
  {
    action: MOVE_ACTIONS.THREE_TWO,
    currentStep: 3,
    nextStep: 2
  },
  {
    action: MOVE_ACTIONS.THREE_ONE,
    currentStep: 3,
    nextStep: 1
  }
];

export const STEPPER_ERRORS = (locale) => ({
  NO_PAYMENT_TYPE_SELECTED: locale['wireImports.mustSelectPaymentType'],
  NO_MAP_FORMAT_SELECTED: locale['wireImports.errorSelectMapType']
});

export const getStepperMoveOption = ({ currentStep, nextStep }) =>
  STEPPER_MOVE_OPTIONS.find(
    (moveOption) => moveOption.currentStep === currentStep && moveOption.nextStep === nextStep
  );
